import { useCallback, useEffect, useState } from 'react';

export interface BrowserAccessPromptState {
  kind: 'audio' | 'microphone';
  title: string;
  message: string;
  requiresMicrophone: boolean;
}

export interface UseVoiceBrowserAccessOptions {
  isInVoiceCall: boolean;
  isMobileCall: boolean;
  isWebrtcCall: boolean;
  liveVoiceCallEnabled: boolean;
  unlockAudio: () => Promise<void>;
}

export interface UseVoiceBrowserAccessReturn {
  browserAccessPrompt: BrowserAccessPromptState | null;
  closeBrowserAccessPrompt: () => void;
  ensureVoiceBrowserAccess: (requireMicrophone: boolean) => Promise<boolean>;
  retryBrowserAccess: () => Promise<void>;
}

/**
 * Mirrors a genuine microphone refusal onto a window event, the same way a
 * grant announces itself as `avatar:mic-access-granted`.
 *
 * A refusal was previously observable only as "a microphone prompt appeared",
 * which also happens when the visitor is merely slow to answer — so consumers
 * could not tell no from not-yet. `reason` distinguishes an outright denial
 * from access already being blocked before we asked.
 */
function announceMicrophoneRefused(reason: 'denied' | 'blocked'): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent('avatar:mic-access-refused', { detail: { reason } }));
}

export function useVoiceBrowserAccess({
  isInVoiceCall,
  isMobileCall,
  isWebrtcCall,
  liveVoiceCallEnabled,
  unlockAudio
}: UseVoiceBrowserAccessOptions): UseVoiceBrowserAccessReturn {
  const [browserAccessPrompt, setBrowserAccessPrompt] = useState<BrowserAccessPromptState | null>(null);

  const showBrowserAccessPrompt = useCallback((prompt: BrowserAccessPromptState) => {
    setBrowserAccessPrompt(prompt);
  }, []);

  const closeBrowserAccessPrompt = useCallback(() => {
    setBrowserAccessPrompt(null);
  }, []);

  const ensureVoiceBrowserAccess = useCallback(async (requireMicrophone: boolean) => {
    await unlockAudio();

    // Best-effort playback unlock for the WebRTC receive element. This used
    // to be a hard gate that returned false on failure — which meant a mic
    // tap could die HERE, before getUserMedia ever ran, so the browser's
    // microphone permission prompt never appeared (Keith's "mic button does
    // nothing" defect, 6 Aug 2026). Playback being blocked is surfaced (so
    // the muted chip shows) but must never veto microphone acquisition.
    const remoteAudio = document.getElementById('remoteAudio') as HTMLAudioElement | null;
    let remoteAudioBlocked = false;
    if (remoteAudio && remoteAudio.srcObject instanceof MediaStream && remoteAudio.paused) {
      try {
        await remoteAudio.play();
      } catch {
        remoteAudioBlocked = true;
      }
    }

    if (!requireMicrophone) {
      if (remoteAudioBlocked) {
        showBrowserAccessPrompt({
          kind: 'audio',
          title: 'Allow audio playback',
          message: 'Audio playback is blocked for this page. Allow audio playback in the browser, then retry access or press Listen again.',
          requiresMicrophone: false
        });
        return false;
      }
      return true;
    }

    if (remoteAudioBlocked) {
      // Let the muted-state surfaces know, but continue to the microphone.
      window.dispatchEvent(new CustomEvent('avatar:audio-access-required', {
        detail: { message: 'Audio playback is blocked for this page. Allow audio playback in the browser, then retry access or press Listen again.' }
      }));
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      showBrowserAccessPrompt({
        kind: 'microphone',
        title: 'Allow microphone access',
        message: 'This browser session cannot access the microphone. Enable microphone access for this page, then retry.',
        requiresMicrophone: true
      });
      return false;
    }

    try {
      if ('permissions' in navigator && navigator.permissions && typeof navigator.permissions.query === 'function') {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (permissionStatus.state === 'denied') {
          showBrowserAccessPrompt({
            kind: 'microphone',
            title: 'Allow microphone access',
            message: 'Microphone access is currently blocked. Use the browser site settings to allow the microphone, then retry.',
            requiresMicrophone: true
          });
          announceMicrophoneRefused('blocked');
          return false;
        }
      }
    } catch {
      // Some browsers either do not support the Permissions API for microphone
      // or throw when queried. We fall back to getUserMedia below.
    }

    // Reuse a cached stream from a recent grant if one exists. AvatarCallSurface
    // (Phase 4) calls getOrAcquireMicStream synchronously inside its click
    // handler so the gesture context is preserved; that helper writes the
    // stream to window.camlinSharedAudioStream. By the time
    // ensureVoiceBrowserAccess runs (via the dispatched primary action), the
    // user gesture is gone — calling getUserMedia again would either
    // re-prompt (Safari) or silently fail. Reusing the cached stream is the
    // fix for the "Voice features disabled (nosip=true)" → silent call loop.
    try {
      const cachedStream = (window as unknown as { camlinSharedAudioStream?: MediaStream | null }).camlinSharedAudioStream;
      if (cachedStream && cachedStream.active && cachedStream.getAudioTracks().some((t) => t.readyState === 'live')) {
        return true;
      }
    } catch {
      // Best-effort — fall through to getUserMedia.
    }

    try {
      // Give the browser permission prompt enough time for a human response
      // before we fall back to the manual retry guidance modal.
      const MICROPHONE_ACCESS_TIMEOUT_MS = 12000;
      let timeoutId: number | null = null;
      const getUserMediaPromise = navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      // Survive a slow permission grant (Keith item 9, 6 Aug 2026): if the
      // visitor takes longer than the race timeout to hit Allow (first-visit
      // users read the dialog), the race below rejects and this call returns
      // false — but the browser prompt is still up and getUserMedia is still
      // pending. When it EVENTUALLY resolves, cache the stream and announce
      // the grant so a pending voice turn can start capture immediately
      // instead of dying. A late denial just stays denied (no event).
      void getUserMediaPromise.then((lateStream) => {
        const holder = window as unknown as { camlinSharedAudioStream?: MediaStream | null };
        if (holder.camlinSharedAudioStream !== lateStream) {
          const previousStream = holder.camlinSharedAudioStream;
          if (previousStream && previousStream.active) {
            // A parallel grant already landed; keep it, drop this one.
            lateStream.getTracks().forEach((track) => track.stop());
          } else {
            holder.camlinSharedAudioStream = lateStream;
          }
        }
        closeBrowserAccessPrompt();
        window.dispatchEvent(new CustomEvent('avatar:mic-access-granted'));
      }).catch(() => {
        // Denied or device error — the visible prompt from the catch below
        // already carries the guidance.
      });
      const stream = await Promise.race([
        getUserMediaPromise,
        new Promise<MediaStream>((_, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new DOMException('Microphone permission request timed out', 'TimeoutError'));
          }, MICROPHONE_ACCESS_TIMEOUT_MS);
        })
      ]).finally(() => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      });
      // CACHE the stream globally so downstream consumers (sttFromMic,
      // voiceCallFeature.makeCall, JsSIP RTCSession, etc.) reuse it
      // instead of calling getUserMedia again. Without this each Listen
      // press triggered 2-3 getUserMedia calls in quick succession; on
      // some browsers (Safari, locked-down corporate Chrome) the second
      // call would fail or re-prompt, leading to the "audio permission
      // issue keeps occurring in the panel" symptom Keith reported on
      // Apr 23 2026.
      //
      // We stop and replace any previously cached stream so we don't
      // leak tracks across reconnects. Consumers should NOT close this
      // stream themselves — they should let the next Listen press or
      // session-end cleanup release it.
      try {
        const previousStream = (window as unknown as { camlinSharedAudioStream?: MediaStream | null }).camlinSharedAudioStream;
        if (previousStream && previousStream !== stream) {
          previousStream.getTracks().forEach((track) => track.stop());
        }
      } catch {
        // Best-effort cleanup — never let a stale-stream close error
        // prevent us from caching the new one.
      }
      (window as unknown as { camlinSharedAudioStream?: MediaStream | null }).camlinSharedAudioStream = stream;
      return true;
    } catch (error) {
      const errorName = error instanceof DOMException ? error.name : '';
      const permissionMessage = (
        errorName === 'NotAllowedError' ||
        errorName === 'PermissionDeniedError' ||
        errorName === 'SecurityError'
      )
        ? 'Microphone access was denied. Allow the microphone for this page, then retry.'
        : errorName === 'TimeoutError'
          ? 'We could not confirm microphone access yet. If Chrome is waiting for permission, allow the microphone from the site controls in the address bar, then retry.'
          : (
            errorName === 'NotFoundError' ||
            errorName === 'DevicesNotFoundError'
          )
            ? 'No microphone was found for this browser session. Connect a microphone, then retry.'
            : (
              errorName === 'NotReadableError' ||
              errorName === 'TrackStartError' ||
              errorName === 'AbortError'
            )
              ? 'The microphone is unavailable or already in use by another application. Free it up, then retry.'
              : 'Microphone access is required before starting the live call. Please allow access and retry.';

      showBrowserAccessPrompt({
        kind: 'microphone',
        title: 'Allow microphone access',
        message: permissionMessage,
        requiresMicrophone: true
      });
      // A timeout is NOT a refusal: the dialog is still up and a late grant
      // announces itself above, so counting it here would report a visitor who
      // simply read the dialog as one who said no.
      if (
        errorName === 'NotAllowedError' ||
        errorName === 'PermissionDeniedError' ||
        errorName === 'SecurityError'
      ) {
        announceMicrophoneRefused('denied');
      }
      return false;
    }
  }, [closeBrowserAccessPrompt, showBrowserAccessPrompt, unlockAudio]);

  const retryBrowserAccess = useCallback(async () => {
    if (!browserAccessPrompt) {
      return;
    }

    const granted = await ensureVoiceBrowserAccess(browserAccessPrompt.requiresMicrophone);
    if (granted) {
      closeBrowserAccessPrompt();
      // Mirror the grant onto a window event so `useAvatarHealth` can
      // clear the 'audio-access-required' issue + flip the status pip
      // back to green. Same mirror pattern used for WebGL and voice
      // call state — keeps the health surface decoupled from this
      // hook's internal state.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('avatar:audio-access-granted'));
      }
    }
  }, [browserAccessPrompt, closeBrowserAccessPrompt, ensureVoiceBrowserAccess]);

  useEffect(() => {
    const handleAudioAccessRequired = (event: CustomEvent<{ message?: string }>) => {
      showBrowserAccessPrompt({
        kind: 'audio',
        title: 'Allow audio playback',
        message: event.detail?.message || 'Audio playback is blocked for this page. Allow audio playback in the browser, then retry.',
        requiresMicrophone: liveVoiceCallEnabled || isWebrtcCall || isMobileCall || isInVoiceCall
      });
    };

    window.addEventListener('avatar:audio-access-required', handleAudioAccessRequired as EventListener);
    return () => {
      window.removeEventListener('avatar:audio-access-required', handleAudioAccessRequired as EventListener);
    };
  }, [isInVoiceCall, isMobileCall, isWebrtcCall, liveVoiceCallEnabled, showBrowserAccessPrompt]);

  return {
    browserAccessPrompt,
    closeBrowserAccessPrompt,
    ensureVoiceBrowserAccess,
    retryBrowserAccess
  };
}

export default useVoiceBrowserAccess;
